import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Modal,
  Grid,
  IconButton,
  Select,
  MenuItem,
  useTheme,
  Typography,
} from "@mui/material";

function ImageScrollPreview({ 
  images = null, 
  sx, 
  showByIndex, 
  image_index, 
  spacing = 0.5, 
  imgHeight = "12rem", 
  imgWidth = null, 
  imgXs=6,
  numImages = 0, 
  garmentUUID = null, 
  overlayColour = null,
  groupRepresentativeBorder=false
}) {
  const theme = useTheme();
  const [garmentImages, setGarmentImages] = useState([]);
  const [hasGarmentImages, setHasGarmentImages] = useState(false);

  useEffect(() => {
    if (images && images.length > 0) {
      setGarmentImages(images);
    } else if (garmentUUID && numImages && numImages > 0) {
      const gImages = Array.from({ length: numImages }, (_, i) => 
        `https://scraped-garment-images.s3.eu-west-2.amazonaws.com/${garmentUUID}-${i + 1}.jpg`
      );
      setGarmentImages(gImages);
    }
  }, [images, numImages, garmentUUID]);

  useEffect(() => {
    setHasGarmentImages(garmentImages && garmentImages.length > 0);
  }, [garmentImages]);

  return hasGarmentImages ? (
    <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
      
      {/* Overlay that correctly covers everything */}
      {overlayColour && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: overlayColour,
            zIndex: 10, // Ensure it appears above the images
            pointerEvents: "none", // Allow clicks to pass through
          }}
        />
      )}

      <Grid
        container
        spacing={spacing}
        sx={{
          position: "relative", // Ensure images are positioned correctly
          height: "100%",
          p: 1,
          alignItems: "center",
          justifyContent: "space-evenly",
          marginBottom: 0,
          overflowY: garmentImages.length < 3 ? "hidden" : "scroll",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "&-ms-overflow-style:": { display: "none" },
          ...sx,
        }}
      >
        {showByIndex ? (
          image_index === 0 || image_index > garmentImages.length ? (
            <Typography variant="h4" sx={{ color: theme.palette.primary[300] }}>
              No image.
            </Typography>
          ) : (
            <img
              loading="eager"
              src={garmentImages[image_index - 1]}
              alt={`Garment ${image_index - 1}`}
              style={{
                width: "100%",
                display: "block", // Removes extra space below image
                margin: "auto", // Center the image within the grid
                objectFit: "contain", // Ensures the image fits inside the given dimensions
                height: imgHeight,
                ...(image_index === 1 && groupRepresentativeBorder && {
                  borderRadius: 4,
                  border: `4px solid ${theme.palette.listedPriceLine[400]}`
                })
              }}
            />
          )
        ) : garmentImages.length > 1 ? (
          garmentImages.map((image, index) => (
            <Grid item xs={imgXs} key={index} sx={{ position: "relative" }}>
              <img
                loading="eager"
                src={image}
                alt={`Garment ${index}`}
                style={{
                  height: imgHeight,
                  width: imgWidth ?? "100%",
                  display: "block", // Removes extra space below image
                  margin: "auto", // Center the image within the grid
                  objectFit: "contain", // Ensures the image fits inside the given dimensions
                  ...(index === 0 && groupRepresentativeBorder && {
                    borderRadius: 4,
                    border: `4px solid ${theme.palette.listedPriceLine[400]}`
                  })
                }}
              />
            </Grid>
          ))
        ) : (
          <img
            loading="eager"
            src={garmentImages[0]}
            alt="Garment"
            style={{
              height: imgHeight,
              width: "100%",
              objectFit: "contain",
              margin: "auto",
              ...(groupRepresentativeBorder && {
                borderRadius: 4,
                border: `4px solid ${theme.palette.listedPriceLine[400]}`
              })
            }}
          />
        )}
      </Grid>
    </Box>
  ) : (
    <Grid container spacing={2}>
      <Typography variant="h4" sx={{ color: theme.palette.primary[300] }}>
        No image.
      </Typography>
    </Grid>
  );
}

export default ImageScrollPreview;
